import React from 'react';
import { Link } from 'react-router-dom';
import {
    FileText,
    CheckCircle2,
    Landmark,
    ShieldCheck,
    Download,
    Mail,
    Phone,
    Award,
    Boxes,
    Truck,
    Users
} from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import ModernCTA from '../../components/CTA/ModernCTA';
import CertificationsTrust from '../../components/CertificationsTrust/CertificationsTrust';

// OEM Partner logos (reused from the Contracts page)
import ciscoLogo from '../../images/brands/cisco-logo.svg';
import dellLogo from '../../images/brands/Dell_Logo.svg.png';
import hpLogo from '../../images/brands/Hewlett-Packard-Logo.wine.png';
import samsungLogo from '../../images/brands/samsung-logo.svg';
import contractHolderLogo from '../../images/certifications/ContractHolder_logo.jpg';

import './ModernNasaSewpPage.scss';

// Gold "Federally Authorized" spotlight for the Cisco OEM logo
const FED_CARD_STYLE = { position: 'relative', border: '2px solid #16a34a', borderRadius: '10px', padding: '0 0.9rem', boxShadow: '0 0 0 4px rgba(22,163,74,0.12)' };
const FED_RIBBON_STYLE = { position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#16a34a', color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(22,163,74,0.35)', zIndex: 2 };

const ModernNasaSewpPage = () => {
    const oemPartners = [
        { img: ciscoLogo, name: 'Cisco' },
        { img: dellLogo, name: 'Dell' },
        { img: hpLogo, name: 'HP' },
        { img: samsungLogo, name: 'Samsung' }
    ];

    // Key contract facts. [TBD] items are JaakTech-specific — fill in real values.
    const contractDetails = [
        { label: 'Contract Number', value: '80TECH26D0472' },
        { label: 'Primary Goods & Services (Contract Scope)', value: 'Category A' },
        { label: 'Period of Performance', value: 'Coming Soon' },
        {
            label: 'Surcharge',
            value: 'The SEWP surcharge for all orders is a 0.34%. The fee is included in the price of all products and is not separately listed on quotes.'
        },
        {
            label: 'Authorized Buyers',
            value: 'All Federal Government Agencies and Authorized Federal Contractors.'
        }
    ];

    const advantages = [
        {
            icon: <ShieldCheck size={22} />,
            text: 'Enterprise-wide IT expertise for the design and integration of multi-faceted solutions.'
        },
        {
            icon: <Boxes size={22} />,
            text: 'Access to a broad catalog of products from hundreds of leading manufacturers.'
        },
        {
            icon: <Users size={22} />,
            text: 'Certified pre-sales engineers and technical staff supporting federal missions. [TBD — add team stats]'
        },
        {
            icon: <Truck size={22} />,
            text: 'Mature logistics and worldwide distribution for fast, reliable fulfillment. [TBD — add distribution stats]'
        },
        {
            icon: <Award size={22} />,
            text: 'Proven past performance delivering value on government-wide acquisition contracts. [TBD — add sales/experience stats]'
        },
        {
            icon: <Landmark size={22} />,
            text: 'Small-business set-aside eligibility with active certifications and SAM.gov registration.'
        }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="NASA SEWP VI"
                subtitle="JaakTech's people, worldwide distribution, in-demand products and integration services help SEWP customers start smart and stay ahead."
                breadcrumbs={[
                    { label: 'Contracts', path: '/contracts' },
                    { label: 'NASA SEWP VI' }
                ]}
            />

            {/* 1. Contract Details */}
            <section className="jk-sewp">
                <div className="jk-sewp__container">
                    <div className="jk-sewp__grid">
                        {/* Left column */}
                        <div className="jk-sewp__intro">
                            <img src={contractHolderLogo} alt="NASA SEWP VI Contract Holder" className="jk-sewp__brand-logo" />
                            <span className="jk-section-label">
                                <FileText size={16} />
                                Contract Details
                            </span>
                            <h2 className="jk-sewp__title">
                                Buy IT Through <span className="highlight">NASA SEWP VI</span>
                            </h2>
                            <p className="jk-sewp__text">
                                Through SEWP, agencies can find an exact fit for their IT needs at the best overall
                                value by choosing the right solutions offered and having them fulfilled quickly.
                                SEWP offers low prices, a low surcharge, and easy, fast ordering using pre-competed
                                government-wide acquisition contracts.
                            </p>

                            <div className="jk-sewp__poc">
                                <h3 className="jk-sewp__poc-heading">Points of Contact</h3>
                                <ul className="jk-sewp__poc-list">
                                    <li>
                                        <span className="jk-sewp__poc-role">SEWP Program Office</span>
                                        <a href="tel:+13012861478" className="jk-sewp__poc-link">
                                            <Phone size={15} /> (301) 286-1478
                                        </a>
                                        <a href="mailto:help@sewp.nasa.gov" className="jk-sewp__poc-link">
                                            <Mail size={15} /> help@sewp.nasa.gov
                                        </a>
                                    </li>
                                    <li>
                                        <span className="jk-sewp__poc-role">SEWP VI Order Contact</span>
                                        <a href="mailto:sewporders@sewp.nasa.gov" className="jk-sewp__poc-link">
                                            <Mail size={15} /> sewporders@sewp.nasa.gov
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Right column */}
                        <div className="jk-sewp__details" style={{ position: 'relative' }}>
                            <div className="jk-sewp__callout">
                                <Landmark size={20} />
                                <span>Federal agencies use the NASA Solutions for Enterprise-Wide Procurement.</span>
                            </div>

                            <dl className="jk-sewp__facts">
                                {contractDetails.map((item, index) => (
                                    <div key={index} className="jk-sewp__fact">
                                        <dt className="jk-sewp__fact-label">{item.label}</dt>
                                        <dd
                                            className={`jk-sewp__fact-value${item.value.includes('[TBD') ? ' jk-sewp__tbd' : ''}`}
                                        >
                                            {item.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>

                            <div className="jk-sewp__oems">
                                <span className="jk-sewp__fed-badge">
                                    <ShieldCheck size={14} /> Federally Authorized
                                </span>
                                <span className="jk-sewp__oems-label">Federal Authorized Partners</span>
                                <div className="jk-sewp__partners-grid">
                                    {oemPartners.map((partner, index) => {
                                        const featured = partner.name === 'Cisco';
                                        return (
                                            <div key={index} className={`jk-sewp__partner${featured ? ' jk-sewp__partner--featured' : ''}`}>
                                                {featured && (
                                                    <span className="jk-sewp__partner-ribbon">
                                                        <ShieldCheck size={10} /> FEDERALLY AUTHORIZED
                                                    </span>
                                                )}
                                                <img src={partner.img} alt={partner.name} className="jk-sewp__partner-logo" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 2. Ordering Guide */}
            <section className="jk-sewp-guide">
                <div className="jk-sewp-guide__container">
                    <div className="jk-sewp-guide__content">
                        <h2 className="jk-sewp-guide__title">SEWP VI Ordering Guide</h2>
                        <p className="jk-sewp-guide__text">
                            Get started with JaakTech's SEWP ordering guide — a step-by-step overview of how to
                            procure IT products and services through our SEWP VI contract.
                        </p>
                    </div>
                    <a
                        className="jk-btn jk-btn--primary jk-sewp-guide__btn"
                        href="/sewp-ordering-guide.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Download size={20} />
                        Download File
                    </a>
                </div>
            </section>

            {/* 3. Fair Opportunity Clause */}
            <section className="jk-sewp-clause">
                <div className="jk-sewp-clause__container">
                    <div className="jk-sewp__header">
                        <span className="jk-section-label">
                            <ShieldCheck size={16} />
                            Fair Opportunity
                        </span>
                        <h2 className="jk-sewp__title">Fair Opportunity Clause</h2>
                    </div>

                    <p className="jk-sewp-clause__intro">
                        The NASA Solutions for Enterprise-Wide Procurement (SEWP) VI contract is a multi-award
                        government-wide acquisition contract (GWAC) and includes the following clause:
                    </p>
                    <p className="jk-sewp-clause__ref">A.1.21. FAIR OPPORTUNITY AND REQUESTS FOR QUOTES</p>

                    <div className="jk-sewp-clause__body">
                        <p>
                            Contractors will be provided fair opportunity at the individual order level as
                            appropriate per FAR Part 16.505(b), including the SEWP RFQ tools. No documentation for
                            the order selection is required to be submitted with the order. All such documentation
                            is to be maintained by the issuing procurement office.
                        </p>
                        <p>
                            The Contractor shall not market, quote, or otherwise offer for sale, under this
                            contract, any products not listed in Attachment F, Pricing Exhibits as defined in the
                            SEWP database of record until the said products are included in the SEWP database and
                            available to all Government end-users.
                        </p>
                        <p>
                            If the Government issues a Request For Information (RFI) as part of market research, the
                            Contractor may provide items not yet listed on their SEWP contract as part of a market
                            research quote if:
                        </p>
                        <ul className="jk-sewp-clause__list">
                            <li>
                                <CheckCircle2 size={18} />
                                <span>All such items are clearly marked as not yet available on their SEWP contract;</span>
                            </li>
                            <li>
                                <CheckCircle2 size={18} />
                                <span>The contractor submits a technology refreshment request to add those products to their contract.</span>
                            </li>
                        </ul>
                        <p>
                            If the Government issues a Request For Quote (RFQ) or a Market Research Request (MRR),
                            the Contractor may only respond with items available on their Contract. If the Contractor
                            has insufficient items on their contract to fully respond to the Formal RFQ, the
                            Contractor must respond with a No Bid. Unless the RFQ specifically allows for partial
                            quotes, the Contractor must respond fully to all requirements specified in the RFQ.
                        </p>
                        <p>
                            When the Contractor markets, quotes, or otherwise offers for sale a product under this
                            contract, the price of each item shall be no greater than the price in Attachment F,
                            Pricing Exhibits as defined in the SEWP database of record at the time the quote is
                            issued.
                        </p>
                        <p>
                            When submitting a quote to a Government end-user, the contractor must clearly state the
                            length of time the quote is valid. The contractor shall honor any order submitted within
                            the stated time period of a quote.
                        </p>
                        <p>
                            When responding to an RFQ or MRR issued from the NASA SEWP on-line quoting system, the
                            Contractor must respond as outlined in Attachment D, Section D.1, On-line Quoting.
                        </p>
                        <p>
                            As a result of the Small Business Jobs Act, the issuing Contracting Officer can set aside
                            an order for small businesses as long as there are two or more with that designation.
                        </p>
                    </div>
                </div>
            </section>

            {/* 4. JaakTech Advantages */}
            <section className="jk-sewp-adv">
                <div className="jk-sewp-adv__container">
                    <div className="jk-sewp__header">
                        <span className="jk-section-label">
                            <Award size={16} />
                            Why JaakTech
                        </span>
                        <h2 className="jk-sewp__title">
                            JaakTech <span className="highlight">Advantages</span>
                        </h2>
                    </div>

                    <div className="jk-sewp-adv__grid">
                        {advantages.map((item, index) => (
                            <div key={index} className="jk-sewp-adv__item">
                                <div className="jk-sewp-adv__icon">{item.icon}</div>
                                <p
                                    className={`jk-sewp-adv__text${item.text.includes('[TBD') ? ' jk-sewp__tbd' : ''}`}
                                >
                                    {item.text}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="jk-sewp-adv__cta">
                        <Link to="/contact" className="jk-btn jk-btn--primary">
                            Let's Talk
                        </Link>
                    </div>
                </div>
            </section>

            {/* 5. Certifications & registrations — reused component */}
            <CertificationsTrust />

            {/* 6. CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernNasaSewpPage;
